// DynamicTable.js
import React, { useState } from "react";
import {
  Button,
  IconButton,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import { BASE_URL, MAX_COLUMN } from "../../global";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";

// DynamicTable Component
const DynamicTable = ({
  initialColumns,
  data,
  setData,
  onColumnLabelChange,
  onColumnDelete,
  srNo = false,
}) => {
  const [columns, setColumns] = useState(initialColumns);

  // Update columns when initialColumns change
  React.useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  const handleDataChange = (rowIndex, accessor, value) => {
    const newData = [...data];
    newData[rowIndex][accessor] = value;
    setData(newData);
  };

  const handleDeleteRow = (rowIndex) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this row?");
    if (!confirmDelete) return;
    const newData = data.filter((_, index) => index !== rowIndex);
    setData(newData);
  };

  const handleColumnLabelChange = (columnIndex, value) => {
    const newColumns = [...columns];
    newColumns[columnIndex].label = value;
    setColumns(newColumns);

    // Only notify parent about label changes, not structure changes
    if (onColumnLabelChange) {
      onColumnLabelChange(columnIndex, value);
    }
  };

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: "1px solid #ccc",
        }}
      >
        <thead>
          <tr>
            {srNo && <th style={styles.th}>Sr.No</th>}
            {columns?.map((column, index) => (
              <th key={column.id} style={styles.th}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    justifyContent: "center",
                  }}
                >
                  <input
                    className="border-1 border-blue-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    type="text"
                    value={column.label}
                    onChange={(e) =>
                      handleColumnLabelChange(index, e.target.value)
                    }
                    style={styles.input}
                    placeholder={`Column ${index + 1}`}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onColumnDelete && onColumnDelete(index)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </div>
              </th>
            ))}
            <th style={styles.th}>Action</th>
          </tr>
        </thead>
        <tbody>
          {data?.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {srNo && <td style={styles.td}>{rowIndex + 1}</td>}
              {columns?.map((column) => (
                <td key={column.id} style={styles.td}>
                  <input
                   className="border-1 border-blue-200 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={row[column.accessor] || ""}
                    onChange={(e) =>
                      handleDataChange(
                        rowIndex,
                        column.accessor,
                        e.target.value
                      )
                    }
                    style={styles.select}
                    placeholder="Enter value"
                  />
                </td>
              ))}
              <td style={styles.td}>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteRow(rowIndex)}
                  color="error"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Styles
const styles = {
  th: {
    padding: "8px",
    border: "1px solid #ccc",
    backgroundColor: "#f5f5f5",
    textAlign: "center",
    minWidth: "120px",
  },
  td: {
    padding: "4px",
    border: "1px solid #ccc",
    textAlign: "center",
    minWidth: "120px",
  },
  input: {
    width: "100%",
    padding: "6px",
    border: "1px solid grey",
    borderRadius: "4px",
    textAlign: "center",
    fontSize: "14px",
    backgroundColor: "#fff",
  },
  select: {
    width: "100%",
    padding: "6px",
    boxSizing: "border-box",
    textAlign: "center",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
  },
};

// Main Component - Optimized for Single Table
const AddTable = ({
  tables,
  setTables,
  jobInspectionId,
  type,
  tableTitle = "Table",
  PrintView,
}) => {
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [columnCount, setColumnCount] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);

  const generateHeaders = (count) => {
    return Array.from({ length: count }, (_, i) => ({
      jobInspectionId: parseInt(jobInspectionId) || 0,
      label: `Column ${i + 1}`,
      colRelation: `c${i + 1}`,
    }));
  };

  const generateColumns = (headers) =>
    headers?.map((col) => ({
      id: col.colRelation,
      accessor: col.colRelation,
      label: col.label,
    }));

  const generateEmptyRow = (headerCount) => {
    const row = {
      jobInspectionId: parseInt(jobInspectionId) || 0,
    };
    // Always generate all MAX_COLUMN columns in the row
    for (let i = 1; i <= MAX_COLUMN; i++) {
      row[`c${i}`] = "";
    }
    return row;
  };

  const handleAddTable = () => {
    if (!columnCount || columnCount < 1 || columnCount > MAX_COLUMN) {
      alert(`Column count must be between 1 and ${MAX_COLUMN}`);
      return;
    }

    const tableId = Date.now();
    const headers = generateHeaders(columnCount);
    const columns = generateColumns(headers);
    const body = [generateEmptyRow(columnCount)]; // Start with one empty row

    setTables([
      {
        id: tableId,
        header: headers,
        columns,
        body,
      },
    ]);

    setColumnDialogOpen(false);
    setColumnCount(1);
  };

  const updateTableData = (newData) => {
    setTables((prev) => {
      const updated = [...prev];
      updated[0].body = newData;
      return updated;
    });
  };

  // Only handle label changes, not structure changes
  const handleColumnLabelChange = (columnIndex, newLabel) => {
    setTables((prev) => {
      const updated = [...prev];
      updated[0].columns[columnIndex].label = newLabel;
      updated[0].header[columnIndex].label = newLabel;
      return updated;
    });
  };

  const addRowToTable = () => {
    if (tables.length === 0) return;
    const table = tables[0];
    const newRow = generateEmptyRow(table.columns.length);
    updateTableData([...table.body, newRow]);
  };

  const addColumnToTable = () => {
    console.log("Add column button clicked"); // Debug log

    if (tables.length === 0) return;
    const table = tables[0];

    if (table.columns.length >= MAX_COLUMN) {
      alert(`Maximum of ${MAX_COLUMN} columns allowed.`);
      return;
    }

    const nextColNumber = table.columns.length + 1;
    const colKey = `c${nextColNumber}`;

    console.log("Adding column:", colKey); // Debug log

    const newHeader = {
      jobInspectionId: parseInt(jobInspectionId) || 0,
      label: `Column ${nextColNumber}`,
      colRelation: colKey,
    };

    const newColumn = {
      id: colKey,
      accessor: colKey,
      label: newHeader.label,
    };

    setTables((prev) => {
      const updated = [...prev];
      const currentTable = updated[0];

      // Add exactly ONE header
      updated[0] = {
        ...currentTable,
        header: [...currentTable.header, newHeader],
        columns: [...currentTable.columns, newColumn],
        body: currentTable.body?.map((row) => ({
          ...row,
          [colKey]: "",
        })),
      };

      console.log("New table structure:", updated[0]); // Debug log
      return updated;
    });
  };

  // Function to delete table data from server
  const deleteTableFromServer = async (jobInspectionId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this table?"
    );
    if (!confirmDelete) return;

    try {
      setIsDeleting(true);

      // Delete jobInspectionId exists and is not 0
      if (jobInspectionId && jobInspectionId !== "0" && jobInspectionId !== 0) {
        const headerDeleteQuery = {
          query: `DELETE FROM dynamicTableHeader WHERE type = ${type} AND jobInspectionId = ${jobInspectionId};`,
        };
        await axiosWithToken.post(`${BASE_URL}dynamic`, headerDeleteQuery);
        const bodyDeleteQuery = {
          query: `DELETE FROM dynamicTableBody WHERE type = ${type} AND jobInspectionId = ${jobInspectionId};`,
        };
        await axiosWithToken.post(`${BASE_URL}dynamic`, bodyDeleteQuery);
        toast.success("Table data deleted successfully!");
      }

      window.location.reload();
    } catch (error) {
      console.error("Error deleting table:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteColumnFromTable = (columnIndex) => {
    if (tables.length === 0) return;

    const confirmDelete = window.confirm("Are you sure you want to delete this column?");
    if (!confirmDelete) return;

    setTables((prev) => {
      const updated = [...prev];
      const table = updated[0];

      const colKey = table.columns[columnIndex].accessor;

      updated[0] = {
        ...table,
        header: table.header.filter((_, i) => i !== columnIndex),
        columns: table.columns.filter((_, i) => i !== columnIndex),
        body: table.body?.map((row) => {
          const { [colKey]: _, ...rest } = row; // remove colKey from row
          return rest;
        }),
      };

      return updated;
    });
  };

  const hasTable = tables.length > 0;

  return (
    <Box p={2}>
      {/* Show Add Table button only if no table exists */}
      {!hasTable && !PrintView && (
        <Box display="flex" gap={2} alignItems="center" mb={2}>
          <Button
            variant="contained"
            onClick={() => setColumnDialogOpen(true)}
            color="primary"
          >
            + Add {tableTitle}
          </Button>
        </Box>
      )}

      {/* Dialog for adding table */}
      <Dialog
        open={columnDialogOpen}
        onClose={() => setColumnDialogOpen(false)}
      >
        <DialogTitle>Enter Number of Columns</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            type="number"
            fullWidth
            label="Column Count"
            value={columnCount}
            onChange={(e) => setColumnCount(Number(e.target.value))}
            inputProps={{ min: 1, max: MAX_COLUMN }}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddTable} variant="contained">
            Add {tableTitle}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Render table if exists */}
      {hasTable && (
        <Box mb={4} border="1px solid #ccc" borderRadius={2} p={2}>
          {/* Action buttons for the table */}
          <Box
            display="flex"
            gap={2}
            mb={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" gap={2}>
              <Button variant="outlined" onClick={addRowToTable}>
                + Add Row
              </Button>
              <Button
                variant="outlined"
                onClick={addColumnToTable}
                disabled={tables[0].columns.length >= MAX_COLUMN}
              >
                + Add Column
              </Button>
            </Box>
            <Button
              variant="outlined"
              color="error"
              onClick={() => deleteTableFromServer(jobInspectionId)}
              disabled={isDeleting}
              size="small"
            >
              {isDeleting ? "Deleting..." : `Delete ${tableTitle}`}
            </Button>
          </Box>

          <DynamicTable
            initialColumns={tables[0].columns}
            data={tables[0].body}
            setData={updateTableData}
            onColumnLabelChange={handleColumnLabelChange}
            onColumnDelete={deleteColumnFromTable}
            srNo
          />
        </Box>
      )}
    </Box>
  );
};

export default AddTable;
